// PROVENANCE: C:\Valentin\src\components\help\FlowDiagram.tsx @ d32c53d (main, H3a), copied 2026-10-11 (H3b-6, Ask Olivia)
// CHANGES: the import of the layout points at web/src/lib/guide/flow-layout.ts (the same text as Valentin's); the styles come
//          from flow-diagram.css, not an inline <style> (Olivia's Content-Security-Policy refuses one, and the boxes drew black).
//          Valentin's own header below names ARIS's "How ARIS works" as its origin.
// PROVENANCE: C:\ARIS\frontend\src\components\workshop\DiagramCanvas.tsx @ 5d37545 (branch post-seb-governance-pi), adapted 2026-10-11
// PROVENANCE: C:\ARIS\frontend\src\pages\HowArisWorksPage.tsx @ 5d37545 (branch post-seb-governance-pi), its Stepper and NodePanel
// CHANGES: H3a's "How it works": no Mantine (plain elements, Tailwind for layout, colours from the --fd-* tokens); the canvas
//          is drawn at its natural size (text stays 14 px and up) inside its own horizontal scroll, which follows the
//          selected box; the stepper, the side card, the key and the accessible list of HowArisWorksPage.tsx are folded
//          in (adapted, not their print parts); every string comes from the caller (labels), so the component has no
//          dictionary of its own; nodes are drawn in the walk's order so Tab follows it; "you are here" marks a box.

/**
 * FlowDiagram — one diagram of "How it works" (H3a; tests/acceptance/h3a-diagrams.ts). Self-contained so the family's other
 * products can copy it with shared/guide/flow-layout.ts (its only import besides React):
 *
 *   <FlowDiagram sheet={…} labels={…} renderWhere={(node) => …} renderRules={(node) => …} current={nodeId} theme="light" />
 *
 * What it draws: the boxes in lanes with their arrows (SVG, no library), each box a button (Enter or Space opens it, Tab
 * walks the boxes in the walk's order); "Walk through the steps" with Previous and Next, which select each box in turn and
 * say where they are (aria-live); the side card of the selected box (what it is, where it is, which rule cards apply: the
 * caller renders the last two); the key to the shapes the diagram uses; and the diagram as an ordered list, the text
 * alternative, one item per box in the same order. Selection is an outline in the accent, never a fill.
 */

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { layout, stepNumbers, type FlowKind, type FlowNode, type FlowPrim, type FlowSheet } from '../../lib/guide/flow-layout';
import './flow-diagram.css';

export interface FlowDiagramLabels {
  /** The key's word for each kind of box. */
  kinds: Record<FlowKind, string>;
  start: string;
  prev: string;
  next: string;
  /** "Step 3 of 17: Requests". */
  position: (n: number, total: number, label: string) => string;
  /** "17 steps, in order". */
  count: (total: number) => string;
  /** The side card before a box is chosen. */
  choose: string;
  where: string;
  rules: string;
  key: string;
  list: string;
  /** "You are here", for the box of the current screen. */
  current: string;
  /** "step 3", in a box's accessible name. */
  badge: (n: number) => string;
}

export interface FlowDiagramProps {
  sheet: FlowSheet;
  labels: FlowDiagramLabels;
  /** Where the box lives in the product (links, or what to open first); omitted: the section is not shown. */
  renderWhere?: (node: FlowNode) => ReactNode;
  /** The rule cards that apply (links); omitted: the section is not shown. */
  renderRules?: (node: FlowNode) => ReactNode;
  /** The box of the current screen, marked "you are here". */
  current?: string | null;
  /** 'light' (the default), 'dark', or 'auto' to follow the reader's colour scheme. */
  theme?: 'light' | 'dark' | 'auto';
}

function PrimEl({ prim, markerPrefix }: { prim: FlowPrim; markerPrefix: string }) {
  switch (prim.t) {
    case 'rect':
      return <rect x={prim.x} y={prim.y} width={prim.w} height={prim.h} rx={prim.rx} className={prim.cls} />;
    case 'circle':
      return <circle cx={prim.cx} cy={prim.cy} r={prim.r} className={prim.cls} />;
    case 'path':
      return <path d={prim.d} className={prim.cls} markerEnd={prim.marker ? `url(#${markerPrefix}-${prim.marker})` : undefined} />;
    case 'text':
      return (
        <text className={prim.cls} fontSize={prim.size} textAnchor={prim.anchor} aria-hidden="true">
          {prim.lines.map((line, i) => <tspan key={i} x={prim.x} y={prim.y + i * prim.lineHeight}>{line}</tspan>)}
        </text>
      );
  }
}

function Markers({ prefix }: { prefix: string }) {
  const one = (style: 'normal' | 'strong' | 'soft', size: number) => (
    <marker key={style} id={`${prefix}-${style}`} viewBox="0 0 10 10" refX={9} refY={5} markerWidth={size} markerHeight={size}
      markerUnits="userSpaceOnUse" orient="auto-start-reverse">
      <path d="M0 0L10 5L0 10z" className={`fd-arrow-${style}`} />
    </marker>
  );
  return <defs>{[one('normal', 10), one('strong', 13), one('soft', 9)]}</defs>;
}

/** A small drawing of one kind's shape, for the key. */
function KindSwatch({ kind }: { kind: FlowKind }) {
  const w = 44;
  const h = 26;
  const shape = (() => {
    switch (kind) {
      case 'person': return <rect x={1} y={1} width={w - 2} height={h - 2} rx={12} className="fd-shape fd-person" />;
      case 'ai': return <rect x={1} y={1} width={w - 2} height={h - 2} rx={5} className="fd-shape fd-ai" />;
      case 'decision': return <path d={`M9 1H${w - 9}L${w - 1} ${h / 2}L${w - 9} ${h - 1}H9L1 ${h / 2}Z`} className="fd-shape fd-decision" />;
      case 'rule': return <><rect x={1} y={1} width={w - 2} height={h - 2} rx={3} className="fd-shape fd-rule" /><rect x={1} y={1} width={5} height={h - 2} className="fd-rule-bar" /></>;
      case 'data': return <><path d={`M1 6A${(w - 2) / 2} 5 0 0 1 ${w - 1} 6V${h - 6}A${(w - 2) / 2} 5 0 0 1 1 ${h - 6}Z`} className="fd-shape fd-data" /><path d={`M1 6A${(w - 2) / 2} 5 0 0 0 ${w - 1} 6`} className="fd-inner" /></>;
      case 'external': return <><rect x={1} y={1} width={w - 2} height={h - 2} rx={3} className="fd-shape fd-external" /><rect x={4} y={4} width={w - 8} height={h - 8} rx={2} className="fd-inner-box" /></>;
      case 'output': return <path d={`M1 1H${w - 1}V${h - 6}C${w - 12} ${h - 12} ${w - 22} ${h} ${w / 2} ${h - 6}C16 ${h - 12} 12 ${h} 1 ${h - 6}Z`} className="fd-shape fd-output" />;
      default: return <rect x={1} y={1} width={w - 2} height={h - 2} rx={6} className="fd-shape fd-step" />;
    }
  })();
  return <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" focusable="false" className="fd-svg shrink-0">{shape}</svg>;
}

export default function FlowDiagram({ sheet, labels, renderWhere, renderRules, current = null, theme = 'light' }: FlowDiagramProps) {
  const drawing = useMemo(() => layout(sheet), [sheet]);
  const numbers = useMemo(() => stepNumbers(sheet), [sheet]);
  const byId = useMemo(() => new Map(sheet.nodes.map((n) => [n.id, n])), [sheet]);
  const [selected, setSelected] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const prefix = `fd-${sheet.id}-${uid}`;
  const cardId = `${prefix}-card`;
  const steps = sheet.steps;
  const index = selected ? steps.indexOf(selected) : -1;
  const node = selected ? byId.get(selected) : undefined;
  const kindsUsed = useMemo(() => [...new Set(sheet.nodes.map((n) => n.kind))], [sheet]);

  // Keep the selected box in view inside the diagram's own scroll (the step-through on a narrow screen).
  useEffect(() => {
    const box = selected ? drawing.boxes[selected] : undefined;
    const el = scrollRef.current;
    if (!box || !el || typeof el.scrollTo !== 'function') return;
    if (box.x < el.scrollLeft || box.x + box.w > el.scrollLeft + el.clientWidth) {
      el.scrollTo({ left: Math.max(0, box.x - (el.clientWidth - box.w) / 2), behavior: 'smooth' });
    }
  }, [selected, drawing]);

  const nameOf = (n: FlowNode) => {
    const parts = [labels.kinds[n.kind], n.label];
    const no = numbers.get(n.id);
    if (no !== undefined) parts.push(labels.badge(no));
    if (n.id === current) parts.push(labels.current);
    return parts.join(', ');
  };

  return (
    <div className="fd-host flex flex-col gap-3" data-theme={theme} data-diagram={sheet.id}>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => index > 0 && setSelected(steps[index - 1] ?? null)} disabled={index <= 0}
          className="fd-btn min-h-[44px] rounded-md px-3 text-sm font-medium disabled:opacity-50">
          {labels.prev}
        </button>
        <button type="button" onClick={() => setSelected(steps[index + 1] ?? null)} disabled={index >= steps.length - 1}
          className="fd-btn fd-btn-primary min-h-[44px] rounded-md px-3 text-sm font-medium disabled:opacity-50">
          {index < 0 ? labels.start : labels.next}
        </button>
        <p className="fd-muted text-sm" aria-live="polite">
          {node && index >= 0 ? labels.position(index + 1, steps.length, node.label) : labels.count(steps.length)}
        </p>
      </div>

      <div ref={scrollRef} className="fd-panel overflow-x-auto overscroll-x-contain">
        <svg className="fd-svg" width={drawing.width} height={drawing.height} viewBox={`0 0 ${drawing.width} ${drawing.height}`}
          role="group" aria-label={sheet.title}>
          <Markers prefix={prefix} />
          {drawing.groups.map((group) => {
            const n = group.nodeId ? byId.get(group.nodeId) : undefined;
            if (!n) {
              return <g key={group.key} aria-hidden="true">{group.prims.map((p, i) => <PrimEl key={i} prim={p} markerPrefix={prefix} />)}</g>;
            }
            const isSelected = selected === n.id;
            return (
              <g
                key={group.key}
                className={`fd-node${isSelected ? ' fd-selected' : ''}${n.id === current ? ' fd-current' : ''}`}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                aria-label={nameOf(n)}
                aria-controls={cardId}
                data-node={n.id}
                onClick={() => setSelected(n.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelected(n.id);
                  }
                }}
              >
                {group.prims.map((p, i) => <PrimEl key={i} prim={p} markerPrefix={prefix} />)}
              </g>
            );
          })}
        </svg>
      </div>

      <div className="grid gap-3 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <section id={cardId} aria-live="polite" className="fd-panel flex flex-col gap-2 p-4">
          {!node ? <p className="fd-muted text-sm">{labels.choose}</p> : (
            <>
              <p className="fd-muted flex flex-wrap items-center gap-2 text-sm">
                <KindSwatch kind={node.kind} />
                <span>{labels.kinds[node.kind]}</span>
                {numbers.has(node.id) && <span>{`· ${labels.badge(numbers.get(node.id) as number)}`}</span>}
                {node.id === current && <span className="fd-accent-text font-semibold">{`· ${labels.current}`}</span>}
              </p>
              <h3 className="text-base font-semibold">{node.label}</h3>
              {node.sub && <p className="fd-muted text-sm">{node.sub}</p>}
              {node.detail && <p className="text-sm">{node.detail}</p>}
              {renderWhere && (
                <div className="flex flex-col gap-1">
                  <h4 className="text-sm font-semibold">{labels.where}</h4>
                  {renderWhere(node)}
                </div>
              )}
              {renderRules && (
                <div className="flex flex-col gap-1">
                  <h4 className="text-sm font-semibold">{labels.rules}</h4>
                  {renderRules(node)}
                </div>
              )}
            </>
          )}
        </section>
        <section aria-label={labels.key} className="fd-panel flex flex-col gap-2 p-4">
          <h3 className="text-sm font-semibold">{labels.key}</h3>
          <ul className="grid gap-2 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2">
            {kindsUsed.map((k) => (
              <li key={k} className="flex items-center gap-2 text-sm" data-kind={k}><KindSwatch kind={k} />{labels.kinds[k]}</li>
            ))}
          </ul>
        </section>
      </div>

      <details className="fd-panel px-4 py-2">
        <summary className="min-h-[44px] cursor-pointer py-2 text-sm font-semibold">{labels.list}</summary>
        <ol className="flex flex-col gap-2 pb-2" data-accessible-list={sheet.id}>
          {steps.map((id) => {
            const n = byId.get(id);
            if (!n) return null;
            return (
              <li key={id} data-node={id} className="text-sm">
                <button type="button" onClick={() => setSelected(id)} className="fd-link text-left font-medium underline">
                  {`${numbers.get(id) ?? ''}. ${n.label}`}
                </button>
                <span className="fd-muted">{` (${labels.kinds[n.kind]})`}</span>
                {n.detail && <span className="block">{n.detail}</span>}
              </li>
            );
          })}
        </ol>
      </details>
    </div>
  );
}
